import { FC, useMemo, useState } from 'react';
import { Box, Grid, Stack, SxProps, Theme } from '@mui/material';
import ArrowIcon from '@/app/assets/icons/accordion-arrow.svg?react';
import {
  CORNER_GRADIENT,
  CORNER_GRADIENT_HOVER,
  GRID_ITEM_FLAT_BACKGROUND,
  GRID_ITEM_ICON_BACKGROUND,
  GRID_ITEM_LABEL_COLOR,
} from './constants';
import { UIGridItemProps } from './types';
import { GridItemRows } from './ui/grid-item-rows';
import { GridItemStat } from './ui/grid-item-stat';

export const UIGridItem: FC<UIGridItemProps> = ({
  title,
  subtitle,
  gridItemProps,
  icon,
  gradient = false,
  expandedRows,
  onClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = !!expandedRows?.length;
  const showRows = expanded && isExpandable;
  const showArrow = isExpandable || !!onClick;
  const handleArrowClick = () => {
    if (isExpandable) {
      setExpanded((prev) => !prev);
      return;
    }
    onClick?.();
  };

  const gridSx: SxProps<Theme> = useMemo(
    () => ({
      padding: '16px',
      gap: '4px',
      borderRadius: '6px',
      background: gradient ? CORNER_GRADIENT : GRID_ITEM_FLAT_BACKGROUND,
      cursor: showArrow ? 'pointer' : 'default',
      position: 'relative',
      isolation: 'isolate',
      transition: 'background-color ease-in-out .4s',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        background: gradient ? CORNER_GRADIENT_HOVER : '#EDF1FE',
        opacity: 0,
        transition: 'opacity ease-in-out .4s',
        pointerEvents: 'none',
        zIndex: -1,
      },
      '&:hover::before': {
        opacity: 1,
      },
    }),
    [gradient, showArrow]
  );

  return (
    <Grid item {...gridItemProps}>
      <Stack onClick={handleArrowClick} sx={gridSx}>
        <Stack direction='row' alignItems='center' gap={1.5}>
          {icon && (
            <Box
              sx={{
                width: '67px',
                height: '67px',
                background: gradient
                  ? 'transparent'
                  : GRID_ITEM_ICON_BACKGROUND,
                border: '1px solid #1555FC1A',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}

          {showRows ? (
            <GridItemRows rows={expandedRows} />
          ) : (
            <GridItemStat title={title} subtitle={subtitle} />
          )}

          {showArrow && (
            <Box
              sx={{ ml: 'auto', '& path': { stroke: GRID_ITEM_LABEL_COLOR } }}
            >
              <ArrowIcon
                style={{
                  width: '15px',
                  height: '15px',
                  transform: `rotate(${showRows ? -90 : 90}deg)`,
                }}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Grid>
  );
};
